/**
 * useVoiceDictation — record from the microphone, transcribe in the
 * background, hand the transcript to the caller.
 *
 * One implementation behind every "talk instead of type" box: the portal's
 * AudioNotesInput widget and the Merlin's-questions answer box on /roadmap
 * (kanban 09e61810: "people need to be able to just record into it, just talk
 * about them"). It composes useAudioRecording (MediaRecorder lifecycle) with
 * the Copilot Whisper endpoint (copilotApi.transcribeAudio) and owns the
 * record → stop → transcribe → append state machine, so callers only render.
 *
 * The caller is never blocked: the transcript arrives through onTranscript
 * whenever Whisper answers, and `cancel()` throws away a recording (or an
 * in-flight transcription) the user no longer wants.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAudioRecording } from './useAudioRecording';
import { transcribeAudio } from '../copilot/services/copilotApi';

/** Whisper's 25 MB cap comfortably fits 15 minutes of opus/aac. */
export const DEFAULT_MAX_DICTATION_SECONDS = 15 * 60;

export const TRANSCRIPTION_FAILED_MESSAGE =
  'Transcription failed. Anything you typed is still here — try recording again.';

export interface UseVoiceDictationOptions {
  /** Receives the trimmed transcript; never called with an empty string. */
  onTranscript: (transcript: string) => void;
  /** Recording auto-stops at this length (default 15 minutes). */
  maxSeconds?: number;
}

export interface UseVoiceDictationReturn {
  /** false when the browser has no MediaRecorder / getUserMedia (hide the mic). */
  isSupported: boolean;
  isRecording: boolean;
  /** true while the microphone permission prompt is open. */
  requestingPermission: boolean;
  /** true from stop until Whisper answers (or fails). */
  transcribing: boolean;
  /** Elapsed recording seconds, ticking while recording. */
  duration: number;
  /** Microphone or transcription error, cleared on the next start(). */
  error: string | null;
  /** Start recording (requests mic permission on first use). */
  start: () => void;
  /** Stop recording; transcription begins in the background. */
  stop: () => void;
  /** start() when idle, stop() when recording. */
  toggle: () => void;
  /** Drop the current recording or in-flight transcription without calling onTranscript. */
  cancel: () => void;
}

/** Append a transcript to whatever the person already typed, on its own line. */
export function appendTranscript(current: string, transcript: string): string {
  const base = current.trim();
  const spoken = transcript.trim();
  if (!spoken) return current;
  return base ? `${base}\n${spoken}` : spoken;
}

/** 0:07, 1:30, 12:05 — the recording timer format. */
export function formatDictationDuration(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function useVoiceDictation({
  onTranscript,
  maxSeconds = DEFAULT_MAX_DICTATION_SECONDS,
}: UseVoiceDictationOptions): UseVoiceDictationReturn {
  const {
    state: recState,
    isRecording,
    duration,
    audioBlob,
    mimeType,
    startRecording,
    stopRecording,
    reset,
    error: recError,
    isSupported,
  } = useAudioRecording();

  const [transcribing, setTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);

  // Latest callback without re-running the transcription effect when the
  // caller re-renders with a new function identity.
  const onTranscriptRef = useRef(onTranscript);
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  // Bumped by cancel(); a transcription started under an older generation
  // drops its result instead of delivering it.
  const generationRef = useRef(0);
  // Set by cancel() while recording: the recorder's onstop still fires and
  // lands a blob, and that blob must be thrown away, not transcribed.
  const discardNextStopRef = useRef(false);

  // Auto-stop at the cap.
  useEffect(() => {
    if (isRecording && duration >= maxSeconds) stopRecording();
  }, [isRecording, duration, maxSeconds, stopRecording]);

  // Recording stopped with audio in hand → transcribe in the background.
  useEffect(() => {
    if (recState !== 'stopped' || !audioBlob || !mimeType) return;
    if (discardNextStopRef.current) {
      discardNextStopRef.current = false;
      reset();
      return;
    }

    const generation = generationRef.current;
    let unmounted = false;
    setTranscribing(true);
    setTranscribeError(null);

    transcribeAudio(audioBlob, mimeType)
      .then((result) => {
        if (unmounted || generation !== generationRef.current) return;
        const transcript = result.transcript.trim();
        if (transcript) onTranscriptRef.current(transcript);
      })
      .catch((err: unknown) => {
        if (unmounted || generation !== generationRef.current) return;
        console.error('Transcription failed:', err);
        setTranscribeError(TRANSCRIPTION_FAILED_MESSAGE);
      })
      .finally(() => {
        if (unmounted) return;
        setTranscribing(false);
        reset();
      });

    return () => {
      unmounted = true;
    };
    // Runs only when the recorder transitions to 'stopped' with a blob.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recState, audioBlob, mimeType]);

  const start = useCallback(() => {
    discardNextStopRef.current = false;
    setTranscribeError(null);
    void startRecording();
  }, [startRecording]);

  const stop = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const toggle = useCallback(() => {
    if (isRecording) stopRecording();
    else start();
  }, [isRecording, stopRecording, start]);

  const cancel = useCallback(() => {
    generationRef.current += 1;
    if (isRecording) {
      discardNextStopRef.current = true;
      stopRecording();
    }
    setTranscribing(false);
    setTranscribeError(null);
    reset();
  }, [isRecording, stopRecording, reset]);

  return {
    isSupported,
    isRecording,
    requestingPermission: recState === 'requesting_permission',
    transcribing,
    duration,
    error: recError ?? transcribeError,
    start,
    stop,
    toggle,
    cancel,
  };
}
